import React, { useState, useEffect, useRef } from "react";
import {
  AlertCircle,
  CheckCircle,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Download,
  Percent,
  IndianRupee,
  Wallet,
  Landmark,
  Handshake,
  BadgeCheck,
  FileClock,
  Banknote,
  Hourglass,
  Clock,
} from "lucide-react";
import { usePayout } from "../../customHooks/Payout/usePayout";
import AppLoader from "../../components/AppLoader/appLoader";
import Pagination from "../../components/Pagination/Pagination";
import { formatDateTime } from "../../components/Constants/Constants";

const searchableFields = [
  { value: "order_id", label: "Order ID" },
  { value: "buyer_app_id", label: "Buyer App ID" },
];

// Utility function to convert snake_case to Title Case
const formatLabel = (fieldName) => {
  return fieldName
    .split("_")
    .map((word) => {
      if (word.toLowerCase() === "msn") {
        return "MSN";
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

// Color palette for rotating through metrics
const colorPalettes = [
  { color: "indigo", bg: "#f0f2ff", icon: <IndianRupee /> },
  { color: "orange", bg: "#fff8ed", icon: <Wallet /> },
  { color: "green", bg: "#f0fff4", icon: <Percent /> },
  { color: "teal", bg: "#f0f7f5", icon: <Handshake /> },
  { color: "blue", bg: "#eef6ff", icon: <Landmark/> },
  { color: "red", bg: "#fff6f6", icon: <FileClock /> },
  { color: "purple", bg: "#f7f0ff", icon: <AlertCircle /> },
  { color: "gray", bg: "#f3f3f4", icon: <Clock /> },
  { color: "olive", bg: "#f7fff4", icon: <Banknote /> },
  { color: "#f59e0b", bg: "#fffbeb", icon: <CheckCircle /> },
  { color: "#ec4899", bg: "#fce7f3", icon: <BadgeCheck /> },
  { color: "navy", bg: "#eef3ff", icon: <Hourglass /> },
];

// Get config for a metric - use rotating palette
const getMetricConfig = (index) => {
  return colorPalettes[index % colorPalettes.length];
};

const Payout = () => {
  const {
    paginatedPayouts, // Updated name to match your useMemo
    loading,
    totalOrderCount,
    filters,
    setFilters,
    metrics,
    ledger,
    balanceDetails,
    totalPages,
    currentPage,
    setCurrentPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    handleItemsPerPageChange,
    itemsPerPageOptions,
  } = usePayout();

  // Today's date in YYYY-MM-DD format for date input max
  const today = new Date().toISOString().slice(0, 10);

  const exportCsv = () => {
    const rows = [];
    // Header (buyer-wise settlement fields)
    rows.push([
      "Buyer App Name",
      "Gross Order Value",
      "Total Settled Amount",
      "Seller Net Payout",
      "Settled MSN Fee",
      "Settled Buyer Fee",
      "Pending Settlement",
      "Pending Order Count",
      "Settled Order Count",
      "Last Settlement Action",
    ]);
    // Data rows
    (paginatedPayouts || []).forEach((item) => {
      rows.push([
        item.buyer_app_name || item.buyer_app_id || "",
        item.gross_order_value != null ? Number(item.gross_order_value) : "",
        item.total_settled_amount != null
          ? Number(item.total_settled_amount)
          : "",
        item.seller_net_payout != null ? Number(item.seller_net_payout) : "",
        item.settled_msn_fee != null ? Number(item.settled_msn_fee) : "",
        item.settled_buyer_fee != null ? Number(item.settled_buyer_fee) : "",
        item.pending_settlement != null ? Number(item.pending_settlement) : "",
        item.pending_order_count != null
          ? Number(item.pending_order_count)
          : "",
        item.settled_order_count != null
          ? Number(item.settled_order_count)
          : "",
        formatDateTime
          ? formatDateTime(item.last_settlement_action)
          : item.last_settlement_action || "",
      ]);
    });

    const csvContent = rows
      .map((r) =>
        r
          .map((c) => {
            if (c === null || c === undefined) return "";
            const s = String(c);
            // escape quotes
            return s.includes(",") || s.includes('"') || s.includes("\n")
              ? `"${s.replace(/"/g, '""')}"`
              : s;
          })
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const now = new Date();
    const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;
    link.href = url;
    link.setAttribute("download", `payouts_${stamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const [showBuyerTable, setShowBuyerTable] = useState(true);
  const [showLedgerTable, setShowLedgerTable] = useState(true);
  const customNInputRef = useRef(null);

  const initialWindowOption = (() => {
    const v = filters?.settlement_window;
    if (v === "" || v === undefined || v === null) return "all";
    const valStr = String(v);
    if (["0", "1", "2", "3"].includes(valStr)) return valStr;
    return "custom";
  })();
  const [windowOption, setWindowOption] = useState(initialWindowOption);

  useEffect(() => {
    if (windowOption === "custom" && customNInputRef.current) {
      customNInputRef.current.focus();
      customNInputRef.current.select();
    }
  }, [windowOption]);

  useEffect(() => {
    const v = filters?.settlement_window;
    if (v === "" || v === undefined || v === null) {
      setWindowOption("all");
      return;
    }
    const valStr = String(v);
    if (["0", "1", "2", "3"].includes(valStr)) {
      setWindowOption(valStr);
    } else if (windowOption !== "custom") {
      setWindowOption("custom");
    }
  }, [filters?.settlement_window]);

  const handleWindowSelectChange = (e) => {
    const val = e.target.value;
    setWindowOption(val);
    if (val === "all") {
      handleFilterChange("settlement_window", "");
    } else if (["0", "1", "2", "3"].includes(val)) {
      handleFilterChange("settlement_window", val);
    } else if (val === "custom") {
      // Prepare for custom input; keep current N or empty for new input
      const current = filters?.settlement_window;
      if (
        current === "" ||
        current === undefined ||
        current === null ||
        ["0", "1", "2", "3"].includes(String(current)) === false
      ) {
        handleFilterChange("settlement_window", "");
      }
    }
  };

  const handleCustomNChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");
    handleFilterChange("settlement_window", digitsOnly);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };
      return newFilters;
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      start_date: "",
      end_date: "",
      settlement_window: "",
      order_id: "",
      order_settlement_status: "",
      buyer_app_id: "",
      search_by: "",
      search_value: "",
    });
    setCurrentPage(1);
  };

  return (
    <div className="m-4">
      <h4 className="fw-bold">Payout Management</h4>

      {/* --- Filters --- */}
      <div
        className="bg-white rounded mb-4"
        style={{
          padding: "20px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e9ecef",
        }}
      >
        <div className="row g-2 align-items-end">
          <div className="col-lg-3 col-md-6">
            <label
              className="form-label mb-2 fw-500"
              style={{ fontSize: "13px", color: "#495057" }}
            >
              Search ID
            </label>
            <div
              className="input-group"
              style={{ boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.04)" }}
            >
              <select
                className="form-select form-select-sm"
                value={filters.search_by}
                onChange={(e) =>
                  handleFilterChange("search_by", e.target.value)
                }
                style={{
                  height: "38px",
                  borderRadius: "4px 0 0 4px",
                  fontSize: "13px",
                  flex: "0 0 auto",
                  minWidth: "8rem",
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  cursor: "pointer",
                }}
              >
                <option value="" style={{ backgroundColor: "white" }}>
                  Field
                </option>
                {searchableFields.map((s) => (
                  <option
                    key={s.value}
                    value={s.value}
                    style={{ backgroundColor: "white" }}
                  >
                    {s.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Enter value..."
                value={filters.search_value}
                onChange={(e) =>
                  handleFilterChange("search_value", e.target.value)
                }
                disabled={!filters.search_by}
                style={{
                  height: "38px",
                  borderRadius: "0 4px 4px 0",
                  fontSize: "13px",
                  border: "2px solid #dee2e6",
                  flex: "1",
                  marginLeft: "0.7rem",
                  backgroundColor: !filters.search_by ? "#e0dddd" : "white",
                  cursor: !filters.search_by ? "not-allowed" : "text",
                }}
              />
            </div>
          </div>

          <div className="col-lg-3 col-md-6">
            <label
              className="form-label mb-2 fw-500"
              style={{ fontSize: "13px", color: "#495057" }}
            >
              Settlement Window
            </label>
            {windowOption === "custom" ? (
              <div className="input-group">
                <span
                  className="input-group-text"
                  style={{ fontSize: "13px", backgroundColor: "#f8f9fa" }}
                >
                  T+
                </span>
                <input
                  ref={customNInputRef}
                  type="number"
                  min="0"
                  step="1"
                  className="form-control form-control-sm"
                  style={{
                    height: "38px",
                    borderRadius: "0 4px 4px 0",
                    fontSize: "13px",
                  }}
                  placeholder="N"
                  value={filters.settlement_window}
                  onChange={handleCustomNChange}
                  onBlur={() => {
                    if (!filters.settlement_window) {
                      setWindowOption("all");
                      handleFilterChange("settlement_window", "");
                    }
                  }}
                />
              </div>
            ) : (
              <select
                className="form-select form-select-sm"
                style={{
                  height: "38px",
                  borderRadius: "4px",
                  fontSize: "13px",
                }}
                value={windowOption}
                onChange={handleWindowSelectChange}
              >
                <option value="all">All Windows</option>
                <option value="0">T+0 (Same Day)</option>
                <option value="1">T+1 (Next Day)</option>
                <option value="2">T+2 (2 Days)</option>
                <option value="3">T+3 (3 Days)</option>
                <option value="custom">Custom (T+N)</option>
              </select>
            )}
          </div>

          <div className="col-lg-2 col-md-6">
            <label
              className="form-label mb-2 fw-500"
              style={{ fontSize: "13px", color: "#495057" }}
            >
              Start Date
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.start_date}
              onChange={(e) => handleFilterChange("start_date", e.target.value)}
              max={today}
              style={{ height: "38px", borderRadius: "4px", fontSize: "13px" }}
            />
          </div>

          <div className="col-lg-2 col-md-6">
            <label
              className="form-label mb-2 fw-500"
              style={{ fontSize: "13px", color: "#495057" }}
            >
              End Date
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.end_date}
              onChange={(e) => handleFilterChange("end_date", e.target.value)}
              min={filters.start_date || ""}
              max={today}
              style={{ height: "38px", borderRadius: "4px", fontSize: "13px" }}
            />
          </div>

          <div className="col-lg-2 col-md-6 d-flex justify-content-end ">
            <button
              type="button"
              className="clearFilter"
              onClick={clearFilters}
            >
              <RotateCcw
                size={16}
                style={{
                  marginRight: "6px",
                  verticalAlign: "middle",
                }}
              />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* --- Metrics --- */}
      <div className="row g-3 mb-4">
        {Object.entries(metrics).map(([key, value], index) => {
          const config = getMetricConfig(index);
          return (
            <MetricCard
              key={key}
              label={formatLabel(key)}
              value={value}
              icon={config.icon}
              color={config.color}
              bg={config.bg}
              isCount={config.isCount}
            />
          );
        })}
      </div>

      {loading ? (
        <AppLoader />
      ) : (
        <>
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white py-3 d-flex align-items-center">
              <span className="fw-bold">Buyer Wise Settlement</span>
              <div className="ms-auto d-flex gap-2">
                <button
                  className="btn btn-sm btn-light border"
                  onClick={exportCsv}
                >
                  <Download size={14} className="me-1" />
                  Export CSV
                </button>
                <button
                  className="btn btn-sm btn-light border"
                  onClick={() => setShowBuyerTable(!showBuyerTable)}
                >
                  {showBuyerTable ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </button>
              </div>
            </div>
            {showBuyerTable && (
              <div className="table-responsive">
                <table
                  className="table table-bordered table-hover align-middle text-center"
                  style={{ marginBottom: "0" }}
                >
                  <thead className="table-light">
                    <tr>
                      <th>Buyer App Name</th>
                      <th>Gross Order Value</th>
                      <th>Total Settled Amount</th>
                      <th>Seller Net Payout</th>
                      <th>Settled MSN Fee</th>
                      <th>Settled Buyer Fee</th>
                      <th>Pending Settlement</th>
                      <th>Pending Order Count</th>
                      <th>Settled Order Count</th>
                      <th>Last Settlement Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPayouts && paginatedPayouts.length > 0 ? (
                      paginatedPayouts.map((item, i) => (
                        <tr key={i} style={{ height: "41px" }}>
                          <td className="small">
                            {item.buyer_app_name || item.buyer_app_id}
                          </td>
                          <td>
                            ₹
                            {Number(
                              item.gross_order_value || 0,
                            ).toLocaleString()}
                          </td>
                          <td>
                            ₹
                            {Number(
                              item.total_settled_amount || 0,
                            ).toLocaleString()}
                          </td>
                          <td className="fw-bold text-primary">
                            ₹
                            {Number(
                              item.seller_net_payout || 0,
                            ).toLocaleString()}
                          </td>
                          <td>
                            ₹
                            {Number(item.settled_msn_fee || 0).toLocaleString()}
                          </td>
                          <td>
                            ₹
                            {Number(
                              item.settled_buyer_fee || 0,
                            ).toLocaleString()}
                          </td>
                          <td>
                            ₹
                            {Number(
                              item.pending_settlement || 0,
                            ).toLocaleString()}
                          </td>
                          <td>{Number(item.pending_order_count || 0)}</td>
                          <td>{Number(item.settled_order_count || 0)}</td>
                          <td>
                            {formatDateTime
                              ? formatDateTime(item.last_settlement_action)
                              : item.last_settlement_action}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          No Records Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination (Exactly like Reports) */}
          {/* {totalOrderCount > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              startItem={startItem}
              endItem={endItem}
              totalItems={totalOrderCount}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={goToPage}
              itemsPerPageOptions={itemsPerPageOptions}
            />
          )} */}
        </>
      )}

      {/* --- Account Ledger Table --- */}
      <div className="card shadow-sm border-0 mt-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <span className="mb-0 fw-bold">NOCA Account Movement</span>

            <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
              Balance: ₹
              {Number(balanceDetails.current_balance || 0).toLocaleString()}
            </span>
          </div>
          <button
            className="btn btn-sm btn-light border"
            onClick={() => setShowLedgerTable(!showLedgerTable)}
          >
            {showLedgerTable ? (
              <ChevronUp size={16} />
            ) : (
              <ChevronDown size={16} />
            )}
          </button>
        </div>
        {showLedgerTable && (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead className="bg-light">
                <tr className="small text-muted">
                  <th className="px-3">DATE</th>
                  <th>CREDIT (+)</th>
                  <th>DEBIT (-)</th>
                  <th>CHARGES</th>
                  <th className="text-end px-3">CLOSING BALANCE</th>
                </tr>
              </thead>
              <tbody className="text-center">
                {ledger && ledger.length > 0 ? (
                  ledger.map((l, idx) => (
                    <tr key={idx} className="small" style={{ height: "32px" }}>
                      <td className="px-3 py-2">{l.date}</td>
                      <td className="text-success">
                        {l.credit !== "0" ? `₹${l.credit}` : "-"}
                      </td>
                      <td className="text-danger">
                        {l.debit !== "0" ? `₹${l.debit}` : "-"}
                      </td>
                      <td className="text-muted">
                        {l.charges !== "0" ? `₹${l.charges}` : "-"}
                      </td>
                      <td className="fw-bold text-end px-3">
                        ₹{l.closing_balance}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {ledger && ledger.length > 0 && (
              <div
                className="bg-light"
                style={{
                  padding: "16px",
                  marginTop: "-1px",
                  borderTop: "1px solid #dee2e6",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  <div className="text-muted">
                    Total Credits:{" "}
                    <span className="text-success">
                      ₹{balanceDetails.total_credited}
                    </span>
                  </div>
                  <div className="text-muted">
                    Total Debits:{" "}
                    <span className="text-danger">
                      ₹{balanceDetails.total_debited}
                    </span>
                  </div>
                  <div className="text-muted">
                    Total Charges:{" "}
                    <span className="text-black fw-bold">
                      ₹{balanceDetails.total_charges}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, icon, color, bg, isCount }) => (
  <div className="col-md-3">
    <div
      className="card border-0 shadow-sm p-3 h-100"
      style={{ borderRadius: "12px" }}
    >
      <div className="d-flex align-items-center mb-2">
        <div
          className="p-2 rounded"
          style={{ backgroundColor: bg, color: color }}
        >
          {icon}
        </div>
        <span className="ms-2 text-muted small fw-medium">{label}</span>
      </div>
      <h4 className="fw-bold mb-0">
        {isCount ? value : `₹ ${Number(value || 0).toLocaleString()}`}
      </h4>
    </div>
  </div>
);

export default Payout;
